import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { menuLabel, menusOf, type MenuItem, type MenuModel } from '../core/menu.ts'
import { useChrome, useChromeState } from './hooks.ts'

/// The Workspace's bar when the page draws the menus (docs/design/menu-bar.md, decisions 9 to 11).
/// The browser's Home has none: its actions are on the page. The model is read only while a menu
/// is open, since a closed bar shows the menus' names alone. The aligner's page draws it too, over
/// its own menu table.
export function AppMenuBar() {
  const shell = useChrome()
  const state = useChromeState()
  if (shell.menuHost.menus !== 'page') return null
  const menus = menusOf(shell.menuHost).map(id => ({ id, label: menuLabel(shell.menuHost, id) }))
  return <MenuBar menus={menus} model={state.menu ? shell.menuModel() : null} open={state.menu} onOpen={menu => (menu ? shell.openMenu(menu) : shell.closeMenu())} onInvoke={id => void shell.invokeMenu(id)} />
}

interface MenuBarProps {
  menus: { id: string; label: string }[]
  model: MenuModel<string> | null
  open: string | null
  onOpen(menu: string | null): void
  onInvoke(id: string): void
}

type Command = Extract<MenuItem, { kind: 'command' }>

/// A submenu beside its item, fixed to the window so a menu that scrolls does not clip it: to the
/// right, or to the left where the window ends, and never below the window's foot.
function placeSubmenu(el: HTMLDivElement | null): void {
  const trigger = el?.previousElementSibling
  if (!el || !trigger) return
  const at = trigger.getBoundingClientRect()
  const width = el.offsetWidth
  el.style.left = `${at.right + width + 4 <= window.innerWidth ? at.right : Math.max(4, at.left - width)}px`
  el.style.maxHeight = `${window.innerHeight - 16}px`
  el.style.top = `${Math.max(8, Math.min(at.top - 5, window.innerHeight - el.offsetHeight - 8))}px`
}

/// The items of the menu element `menu` itself, a submenu's left out.
const itemsOf = (menu: Element | null): HTMLElement[] =>
  menu ? [...menu.querySelectorAll<HTMLElement>('[role^="menuitem"]')].filter(el => el.parentElement?.closest('[role="menu"]') === menu) : []

/// The next item after `from` whose label starts with `letter`, wrapping.
function typeAhead(items: HTMLElement[], from: number, letter: string): HTMLElement | null {
  const l = letter.toLowerCase()
  for (let i = 1; i <= items.length; i++) {
    const item = items[(from + i) % items.length]!
    if ((item.querySelector('.label')?.textContent ?? item.textContent ?? '').trim().toLowerCase().startsWith(l)) return item
  }
  return null
}

const isLetter = (e: React.KeyboardEvent) => e.key.length === 1 && /\S/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey

export function MenuBar({ menus, model, open, onOpen, onInvoke }: MenuBarProps) {
  const bar = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  /// The bar's one tab stop.
  const [roving, setRoving] = useState(0)
  /// The submenu open in the menu, by label.
  const [sub, setSub] = useState<string | null>(null)
  /// Which item a menu opened by key starts on.
  const start = useRef<'first' | 'last' | 'none'>('first')
  const buttons = () => [...(bar.current?.querySelectorAll<HTMLElement>(':scope > [role="menuitem"]') ?? [])]
  const index = (id: string) => menus.findIndex(m => m.id === id)

  const was = useRef(open)
  useLayoutEffect(() => {
    setSub(null)
    const closed = was.current !== null && open === null
    was.current = open
    // A menu closed with nowhere to give the focus back to, as after Shift+Tab into the bar,
    // leaves it on the bar; one that gave the focus back starts the bar on File next time.
    if (closed) {
      if (document.activeElement === null || document.activeElement === document.body) buttons()[roving]?.focus()
      else if (!bar.current?.contains(document.activeElement)) setRoving(0)
    }
    if (!open) return
    const i = index(open)
    setRoving(i)
    const at = buttons()[i]?.getBoundingClientRect()
    const el = panel.current
    if (!el || !at) return
    // The menu stays inside the window and scrolls when the window is too short for it.
    el.style.top = `${at.bottom}px`
    el.style.maxHeight = `${Math.max(120, window.innerHeight - at.bottom - 8)}px`
    el.style.left = `${Math.max(4, Math.min(at.left, window.innerWidth - el.offsetWidth - 4))}px`
    // Opened by a click, the menu itself takes the keys, so Down reaches its first item.
    const items = itemsOf(el)
    const first = start.current === 'none' ? el : start.current === 'last' ? items.at(-1) : items[0]
    start.current = 'first'
    first?.focus()
  }, [open])

  // A press outside the bar and its menus closes the menu and lands where it was pointed.
  useEffect(() => {
    if (!open) return
    const listener = (e: MouseEvent) => {
      if (e.target instanceof Element && !e.target.closest('.menubar, .barmenu')) onOpen(null)
    }
    document.addEventListener('mousedown', listener, true)
    return () => document.removeEventListener('mousedown', listener, true)
  }, [open, onOpen])

  const focusMenu = (i: number) => {
    const n = (i + menus.length) % menus.length
    setRoving(n)
    buttons()[n]?.focus()
  }
  const openAt = (i: number, from: 'first' | 'last' | 'none' = 'first') => {
    start.current = from
    onOpen(menus[(i + menus.length) % menus.length]!.id)
  }

  const onBarKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') focusMenu(i + 1)
    else if (e.key === 'ArrowLeft') focusMenu(i - 1)
    else if (e.key === 'Home') focusMenu(0)
    else if (e.key === 'End') focusMenu(-1)
    else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') openAt(i)
    else if (e.key === 'ArrowUp') openAt(i, 'last')
    else if (e.key === 'Escape') onOpen(null)
    else if (isLetter(e)) {
      const to = typeAhead(buttons(), i, e.key)
      if (to) focusMenu(buttons().indexOf(to))
    } else return
    e.preventDefault()
    e.stopPropagation()
  }

  const onMenuKey = (e: React.KeyboardEvent) => {
    const current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const menu = current?.closest('[role="menu"]') ?? panel.current
    const items = itemsOf(menu)
    const at = current ? items.indexOf(current) : -1
    const inSub = menu !== panel.current
    const go = (i: number) => items[(i + items.length) % items.length]?.focus()
    const trigger = current?.getAttribute('aria-haspopup') === 'menu' ? current : null
    const openSub = () => {
      if (!trigger) return
      setSub(trigger.dataset.submenu ?? null)
      requestAnimationFrame(() => itemsOf(trigger.parentElement?.querySelector('[role="menu"]') ?? null)[0]?.focus())
    }
    const closeSub = () => {
      const back = menu?.parentElement?.querySelector<HTMLElement>(':scope > [aria-haspopup="menu"]')
      setSub(null)
      back?.focus()
    }
    if (e.key === 'ArrowDown') go(at + 1)
    else if (e.key === 'ArrowUp') go(at < 0 ? -1 : at - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(-1)
    else if (e.key === 'ArrowRight') {
      if (trigger) openSub()
      else openAt(index(open!) + 1)
    } else if (e.key === 'ArrowLeft') {
      if (inSub) closeSub()
      else openAt(index(open!) - 1)
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (trigger) openSub()
      else if (current?.dataset.command && current.getAttribute('aria-disabled') !== 'true') onInvoke(current.dataset.command)
    } else if (e.key === 'Escape') {
      if (inSub) closeSub()
      else onOpen(null)
    } else if (e.key === 'Tab') {
      onOpen(null)
    } else if (isLetter(e)) {
      typeAhead(items, at, e.key)?.focus()
    } else return
    e.preventDefault()
    e.stopPropagation()
  }

  const command = (item: Command) => (
    <div
      key={item.id}
      role={item.checked === null ? 'menuitem' : 'menuitemcheckbox'}
      aria-checked={item.checked ?? undefined}
      aria-disabled={item.enabled ? undefined : true}
      aria-describedby={item.reason ? `reason-${item.id}` : undefined}
      tabIndex={-1}
      className={`mitem${item.reason ? ' has-reason' : ''}`}
      data-command={item.id}
      onMouseEnter={e => e.currentTarget.focus()}
      onClick={() => item.enabled && onInvoke(item.id)}
    >
      <span className="check" aria-hidden>{item.checked ? '✓' : ''}</span>
      <span className="label">{item.label}</span>
      {item.shown ? <kbd>{item.shown}</kbd> : null}
      {item.reason ? <small className="mreason" id={`reason-${item.id}`}>{item.reason}</small> : null}
    </div>
  )

  const list = (items: MenuItem[]) => items.map((item, i) => {
    if (item.kind === 'separator') return <div key={`sep-${i}`} role="separator" className="menubreak" />
    if (item.kind === 'command') return command(item)
    const expanded = sub === item.label
    return (
      <div key={`sub-${item.label}`} className="subwrap">
        <div role="menuitem" aria-haspopup="menu" aria-expanded={expanded} tabIndex={-1} className="mitem" data-submenu={item.label}
          onMouseEnter={e => { e.currentTarget.focus(); setSub(item.label) }} onClick={() => setSub(expanded ? null : item.label)}>
          <span className="check" aria-hidden />
          <span className="label">{item.label}</span>
          <span className="arrow" aria-hidden>▸</span>
        </div>
        {expanded ? <div ref={placeSubmenu} role="menu" aria-label={item.label} className="menu barmenu submenu">{list(item.items)}</div> : null}
      </div>
    )
  })

  const shown = open ? model?.menus.find(m => m.id === open) : null
  return (
    <>
      <div ref={bar} className="menubar" role="menubar" aria-label="Menus" onBlur={e => { if (!open && !(e.relatedTarget instanceof Element && e.relatedTarget.closest('.menubar, .barmenu'))) setRoving(0) }}>
        {menus.map((m, i) => (
          <div
            key={m.id}
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open === m.id}
            tabIndex={i === roving ? 0 : -1}
            id={`menubar-${m.id}`}
            data-menu={m.id}
            onMouseDown={e => e.preventDefault()}
            onClick={() => (open === m.id ? onOpen(null) : openAt(i, 'none'))}
            onMouseEnter={() => { if (open && open !== m.id) openAt(i, 'none') }}
            onKeyDown={e => onBarKey(e, i)}
          >
            {m.label}
          </div>
        ))}
      </div>
      {shown ? (
        <div ref={panel} className="menu barmenu" role="menu" tabIndex={-1} id={`menu-${shown.id}`} aria-labelledby={`menubar-${shown.id}`} onKeyDown={onMenuKey}>
          {list(shown.items)}
        </div>
      ) : null}
    </>
  )
}
